
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Thermometer, Fan, ArrowDown, ArrowUp, Zap, Gauge } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChillerDataCardProps {
  title: string;
  compact?: boolean;
  className?: string;
  chillerData?: {
    inletTemp: number;
    outletTemp: number;
    efficiency: number;
    powerConsumption: number;
    coolingLoad: number;
    cop: number;
    fanSpeed: number;
  };
  chillerNumber?: number; // 1, 2, or 3 corresponding to Main, Secondary, or Auxiliary
}

const ChillerDataCard: React.FC<ChillerDataCardProps> = ({ 
  title, 
  compact = false,
  className = "",
  chillerNumber = 1
}) => {
  // Default chiller data based on chillerNumber
  let chillerData;
  
  switch(chillerNumber) {
    case 1: // Main Chiller
      chillerData = {
        inletTemp: 12.2,
        outletTemp: 6.8,
        efficiency: 84.5,
        powerConsumption: 139.2,
        coolingLoad: 490.5,
        cop: 3.52,
        fanSpeed: 65
      };
      break;
    case 2: // Secondary Chiller
      chillerData = {
        inletTemp: 12.6,
        outletTemp: 6.5,
        efficiency: 86.9,
        powerConsumption: 144.6,
        coolingLoad: 522.1,
        cop: 3.61,
        fanSpeed: 72
      };
      break;
    case 3: // Auxiliary Chiller
      chillerData = {
        inletTemp: 12.5,
        outletTemp: 6.9,
        efficiency: 84.1,
        powerConsumption: 137.8,
        coolingLoad: 480.4,
        cop: 3.49,
        fanSpeed: 63
      };
      break;
    default:
      chillerData = {
        inletTemp: 12.2,
        outletTemp: 6.8,
        efficiency: 84.5,
        powerConsumption: 139.2,
        coolingLoad: 490.5,
        cop: 3.52,
        fanSpeed: 65
      };
  }

  return (
    <Card className={cn(
      "border-l-4 h-full",
      "bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow",
      "border-l-cyan-500", 
      className
    )}>
      <CardHeader className={compact ? "p-3" : "pb-2"}>
        <CardTitle className={cn(
          compact ? "text-base" : "text-lg",
          "text-gray-800 dark:text-gray-100"
        )}>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? "p-3 pt-0" : "pt-0"}>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900">
              <ArrowDown className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Inlet Temp</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.inletTemp}°C</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900">
              <ArrowUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Outlet Temp</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.outletTemp}°C</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900">
              <Gauge className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Efficiency</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.efficiency}%</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900">
              <Fan className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Fan Speed</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.fanSpeed}%</p>
            </div>
          </div>
        </div>

        {!compact && (
          <div className="mt-4 grid grid-cols-3 gap-2 border-t border-gray-200 dark:border-gray-700 pt-3 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Power</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.powerConsumption} kW</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Load</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.coolingLoad} kW</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">COP</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{chillerData.cop}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChillerDataCard;
